import { useEffect } from 'react';
import { CalendarRange } from 'lucide-react';
import { useActiveEvents } from '@/hooks/useEvents';
import { Select } from '@/components/ui/Select';
import { Chip } from '@/components/ui/Chip';
import { eventColorClasses } from '@/lib/eventColors';

/**
 * EventSelector — lets the user (cashier or owner) pick which event is
 * "active" on this device. Used in the TopBar so the cashier always knows
 * which event is being worked on.
 *
 * Phase 4: now uses the React Query hook (useActiveEvents) and shows the
 * event code alongside the name.
 */
export function EventSelector({
  value,
  onChange,
}: {
  value: string | null;
  onChange: (id: string | null, name?: string) => void;
}) {
  const { data: events, isLoading, isSuccess } = useActiveEvents();

  // The active event is per-device localStorage. Closing an event clears the
  // pointer on the machine that closed it, and nowhere else — so a cashier's
  // tablet keeps pointing at an event the owner closed on their laptop. The
  // selector below already renders that as "— No event selected —" (the id is
  // not in the list), while every page still reads the stale id out of the
  // store: POS refuses with a server error, and returns used to corrupt the
  // closed event's stock reconciliation outright. Reconcile the two by
  // dropping a pointer the server no longer lists as active.
  //
  // Only on a successful fetch: a failed or in-flight query says nothing about
  // whether the event is still open, and clearing on it would kick a working
  // cashier off their event whenever the network blinked.
  useEffect(() => {
    if (!isSuccess || !value) return;
    if (!(events ?? []).some((e) => e.id === value)) onChange(null);
  }, [isSuccess, events, value, onChange]);

  if (isLoading) {
    return (
      <div className="h-9 w-40 rounded bg-line/30 animate-pulse" aria-label="Loading events" />
    );
  }

  const list = events ?? [];

  if (list.length === 0) {
    return (
      <span className="inline-flex items-center gap-1.5 text-small text-ink-subtle italic">
        <CalendarRange size={14} />
        No active event — start one first
      </span>
    );
  }

  const selected = list.find((e) => e.id === value);

  return (
    <div className="inline-flex items-center gap-2">
      <Select
        value={value ?? ''}
        onChange={(value) => {
          const id = value || null;
          const evt = list.find((ev) => ev.id === id);
          onChange(id, evt?.name);
        }}
        className="min-w-[220px]"
        aria-label="Select active event"
      >
        <option value="">— No event selected —</option>
        {list.map((evt) => (
          <option key={evt.id} value={evt.id}>
            {evt.name} ({evt.code})
          </option>
        ))}
      </Select>
      {selected && (
        // The event's colour rides on the chip: with several events running,
        // this is how "am I on the right one" is answered at a glance.
        <Chip variant="info" className={`hidden lg:inline-flex ${eventColorClasses(selected.color).soft}`}>
          <span className={`w-2 h-2 rounded-full ${eventColorClasses(selected.color).dot}`} aria-hidden />
          {selected.code}
        </Chip>
      )}
    </div>
  );
}
